import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { AlertTriangle, Upload } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { BillingsMatrixTable } from '@/components/sales-trackers/BillingsMatrix'
import { FiscalYearPicker } from '@/components/sales-trackers/FiscalYearPicker'
import { TargetVsActual } from '@/components/sales-trackers/TargetVsActual'
import { TargetsDialog } from '@/components/sales-trackers/TargetsDialog'
import { NoBillingsYet } from '@/components/sales-trackers/no-billings-yet'
import { BILLINGS_UPLOAD_PATH, isNoBillingsYet } from '@/components/sales-trackers/billings-empty'
import {
  useBillingsFiscalYears,
  useBillingsImports,
  useBillingsOverview,
  useBillingsTargets,
} from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { explainError } from '@/lib/explain-error'
import { useBillingsPeriod, periodQuery, periodLabel } from '@/hooks/use-billings-period'
import { exportToCSV } from '@/utils/csvExport'

/**
 * Company overview — the four blocks from Jason's dashboard.
 *
 * All four come from ONE request. They are read side by side and must agree with
 * each other, which four separate requests cannot guarantee if an upload lands
 * between them.
 *
 * These are QuickBooks actuals, not pipeline. Nothing here should ever be added to
 * an opportunity figure: the same revenue would be counted twice.
 */
export default function SalesTrackersOverview() {
  const topClients = useOverflowFocus('Top clients by billings')
  useDocumentTitle('Sales Trackers')

  const years = useBillingsFiscalYears()
  // The period lives in the URL so it survives the remount when somebody clicks through
  // to a rep, and so a filtered view can be pasted into a message.
  const period = useBillingsPeriod(years.data)
  const { fy, granularity, month, quarter, setPeriod } = period
  const overview = useBillingsOverview({ fy, ...periodQuery(period) })
  // Uploading left the nav band, so Overview has to be the place it is reachable from
  // and the place that says when it last happened. Same hook the Upload page uses, so
  // no new endpoint and no second source of truth for "when did data last arrive".
  const imports = useBillingsImports()
  const lastImport = imports.data?.imports?.[0]

  // Attainment arrives inside the overview payload, so the card never disagrees with
  // the figures beside it. The target ROWS are a separate request because only the
  // dialog needs them - and `enabled` is what makes that true rather than just claimed:
  // an earlier version said "only when it opens" while fetching on every page load.
  const [editingTargets, setEditingTargets] = useState(false)
  const companyTargets = useBillingsTargets(fy, 'company', editingTargets)

  const effectiveFy = fy ?? overview.data?.fiscal_year
  const shownPeriod = periodLabel(granularity, {
    fy: effectiveFy,
    month,
    quarter,
    monthLabel: overview.data?.months?.[0]?.label,
  })
  const money = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 })

  if (overview.isError) {
    return (
      <section className="p-6 space-y-4" aria-labelledby="sales-trackers-heading">
        <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
          Sales Trackers
        </h1>
        {isNoBillingsYet(overview.error) ? (
          <NoBillingsYet what="The company figures" />
        ) : (
          <>
            <Alert variant="destructive">
              <AlertTitle>Could not load the overview</AlertTitle>
              <AlertDescription>
                {explainError(overview.error, 'Something went wrong loading the sales overview.')}
              </AlertDescription>
            </Alert>
            {/* Uploading is reachable from here and nowhere else in the nav, so the link
                has to survive a failure too — not only the empty case. */}
            <Button asChild variant="outline">
              <Link to={BILLINGS_UPLOAD_PATH}>
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                Upload QuickBooks export
              </Link>
            </Button>
          </>
        )}
      </section>
    )
  }

  const data = overview.data
  const unclassified = data?.split.rows.find((r) => r.category === 'unclassified')

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
            Sales Trackers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Net billings from QuickBooks. These are invoiced actuals, not pipeline.
          </p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/sales-trackers/upload">
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                Upload QuickBooks export
              </Link>
            </Button>
            {data && (
              <Button
                variant="outline"
                onClick={() =>
                  exportToCSV({
                    filename: `sales-by-rep-${shownPeriod.replace(/[^\w-]+/g, '-')}.csv`,
                    // Exact values, not the rounded figures shown on screen.
                    data: data.by_rep.rows.map((r) => ({
                      Rep: r.label,
                      ...Object.fromEntries(
                        data.months.map((m) => [m.label, r.months[m.key] ?? 0])
                      ),
                      [`${shownPeriod} total`]: r.total,
                    })),
                  })
                }
                aria-label="Export the rep breakdown to CSV"
              >
                Export CSV
              </Button>
            )}
          </div>
          {lastImport && (
            <p className="text-xs text-muted-foreground">
              Last upload: {lastImport.period_label ?? lastImport.file_name} (
              {lastImport.source_entity}), {lastImport.row_count.toLocaleString()} rows,{' '}
              {new Date(lastImport.created_date).toLocaleDateString('en-CA')}
            </p>
          )}
        </div>
      </div>

      {years.data && years.data.length > 0 && (
        <FiscalYearPicker
          years={years.data}
          value={effectiveFy}
          onChange={(next) => setPeriod({ fy: next })}
          period={{ granularity, month, quarter, onChange: setPeriod }}
        />
      )}

      {/* Announce loading rather than only spinning. */}
      <p role="status" aria-live="polite" className="sr-only">
        {overview.isFetching ? 'Loading sales figures' : data ? 'Sales figures loaded' : ''}
      </p>

      {overview.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && (
        <>
          {/* Unclassified is surfaced, not hidden: in the spreadsheet these rows
              count toward the total but appear on neither side of the split, and
              nothing says so. */}
          {unclassified && unclassified.total !== 0 && (
            <Alert role="note">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>{money(unclassified.total)} is not categorised</AlertTitle>
              <AlertDescription>
                Some sales accounts have no category, so they count toward the total but appear in
                neither Software nor Service.{' '}
                <a href="/sales-trackers/attribution" className="underline">
                  Categorise them
                </a>
                .
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Total billings" value={money(data.by_rep.totals.total)} />
            <StatCard
              label="Software"
              value={money(data.split.rows.find((r) => r.category === 'software')?.total ?? 0)}
            />
            <StatCard
              label="Remediation"
              value={money(data.split.rows.find((r) => r.category === 'remediation')?.total ?? 0)}
            />
            <StatCard
              label="Other services"
              value={money(
                data.split.rows.find((r) => r.category === 'other_services')?.total ?? 0
              )}
            />
          </div>

          {/* Between the headline stats and the grids: the question Jason opens the
              page to answer is whether the company is ahead or behind, so it sits
              above the month-by-month detail rather than under it. */}
          <TargetVsActual
            attainment={data.targets}
            monthCount={data.months.length}
            onEdit={() => setEditingTargets(true)}
            periodLabel={shownPeriod}
            isSingleMonth={granularity === 'month'}
          />

          <Card>
            <CardHeader>
              <CardTitle>Total sales by rep</CardTitle>
            </CardHeader>
            <CardContent>
              <BillingsMatrixTable
                matrix={data.by_rep}
                dimensionLabel="Rep"
                caption={`Total sales by rep for ${shownPeriod}, by month`}
                // By id where the rep is a person, falling back to the label only for
                // the buckets that are not. A label is a snapshot of a current name,
                // and two reps sharing one would land on the same page.
                linkFor={(row) =>
                  `/sales-trackers/reps/${encodeURIComponent(row.rep_user_id ?? row.label)}`
                }
                noteFor={(row) =>
                  row.house_total ? `incl. ${money(row.house_total)} house` : null
                }
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Product category by month</CardTitle>
            </CardHeader>
            <CardContent>
              <BillingsMatrixTable
                matrix={data.by_product}
                dimensionLabel="Product"
                caption={`Billings by product category for ${shownPeriod}, by month`}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Software, service and remediation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <BillingsMatrixTable
                matrix={data.split}
                dimensionLabel="Category"
                caption={`Software, service and remediation split for ${shownPeriod}`}
              />
              {data.split.percentages && (
                <BillingsMatrixTable
                  matrix={{ ...data.split, rows: data.split.percentages }}
                  dimensionLabel="Share"
                  caption={`Share of billings by category for ${shownPeriod}`}
                  asPercent
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top clients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto" ref={topClients.ref} {...topClients.regionProps}>
                <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <caption className="sr-only">
                    Top clients by billings for FY{data.fiscal_year}
                  </caption>
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left font-medium py-2 pr-3">
                        #
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Client
                      </th>
                      <th scope="col" className="text-right font-medium py-2 px-3">
                        Billings
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pl-3">
                        Main rep
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_clients.rows.map((c) => (
                      <tr key={c.customer_name} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 text-muted-foreground">{c.rank}</td>
                        <th scope="row" className="text-left font-normal py-1.5 pr-4">
                          {c.customer_name}
                        </th>
                        <td className="text-right py-1.5 px-3">{money(c.amount)}</td>
                        <td className="py-1.5 pl-3">{c.rep_label ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {data && (
        <TargetsDialog
          open={editingTargets}
          onOpenChange={setEditingTargets}
          months={data.months}
          existing={companyTargets.data?.targets ?? []}
          scope="company"
        />
      )}
    </section>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </p>
      </CardContent>
    </Card>
  )
}
